import { inject, Injectable } from '@angular/core';
import { ExpressNote, ExpressNotesDataService } from '@ffd/api-client';
import { DataState, FfdComponentStore } from '@libs/utils';
import { TagEvent, TagPusherService, ToastService } from '@libs/shared-services';
import { iosMicActions } from '@libs/ios-mic';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { tapResponse } from '@ngrx/operators';
import { debounceTime, Observable, switchMap, tap, withLatestFrom } from 'rxjs';
import type { FfdNoteGroup, FfdNoteItem } from './ffd-notes-screen.interface';

interface FfdNotesState {
  expressNotes: ExpressNote[];
  searchTerm: string | null;
  listStatus: DataState;
  infiniteScrollStatus: DataState;
  offset: number;
  totalCount: number | null;
  activeNoteGroupId: string | null;
  selectedNoteId: string | null;
  editedNoteId: string | null;
  isNoteSaving: boolean;
  isNoteSaved: boolean;
}

interface FfdNotesViewModel {
  noteGroups: FfdNoteGroup[];
  searchTerm: string | null;
  loading: boolean;
  infiniteScrollLoading: boolean;
  activeNoteGroupId: string | null;
  selectedNoteId: string | null;
  editedNoteId: string | null;
  selectedNote?: FfdNoteItem;
  isNoteSaving: boolean;
  isNoteSaved: boolean;
}

const defaultState: FfdNotesState = {
  expressNotes: [],
  searchTerm: null,
  listStatus: DataState.Initial,
  infiniteScrollStatus: DataState.Initial,
  offset: 0,
  totalCount: null,
  activeNoteGroupId: null,
  selectedNoteId: null,
  editedNoteId: null,
  isNoteSaving: false,
  isNoteSaved: false,
};

const SEARCH_DEBOUNCE_TIME_MS = 500;
const PAGE_SIZE = 10;

@Injectable({
  providedIn: 'root',
})
export class FfdNotesScreenService extends FfdComponentStore<FfdNotesState, FfdNotesViewModel> {
  private readonly expressNotesDataService = inject(ExpressNotesDataService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly store = inject(Store);

  constructor() {
    super(defaultState);
  }

  public override init(): void {
    this.setSearchTerm('');
    this.getExpressNotes();

    this.store.dispatch(iosMicActions.initialize());
  }

  protected override mapStateToViewModel(state: FfdNotesState): FfdNotesViewModel {
    const noteGroups = this.groupNotesByDate(state.expressNotes);

    return {
      noteGroups,
      loading: state.listStatus === DataState.Loading,
      searchTerm: state.searchTerm,
      infiniteScrollLoading: state.infiniteScrollStatus === DataState.Loading,
      activeNoteGroupId: state.activeNoteGroupId,
      selectedNoteId: state.selectedNoteId,
      selectedNote: noteGroups
        .find((noteGroup) => noteGroup.visitId === state.activeNoteGroupId)
        ?.notes.find((note) => note.id === state.selectedNoteId),
      editedNoteId: state.editedNoteId,
      isNoteSaving: state.isNoteSaving,
      isNoteSaved: state.isNoteSaved,
    };
  }

  private groupNotesByDate(notes: ExpressNote[]): FfdNoteGroup[] {
    // Group notes by date (YYYY-MM-DD)
    const groupedMap = new Map<string, ExpressNote[]>();

    for (const note of notes) {
      const dateKey = note.createdAt.split('T')[0]; // Extract YYYY-MM-DD
      if (!groupedMap.has(dateKey)) {
        groupedMap.set(dateKey, []);
      }
      groupedMap.get(dateKey)!.push(note);
    }

    // Convert map to FfdNoteGroup array
    const noteGroups: FfdNoteGroup[] = [];
    for (const [dateKey, groupNotes] of groupedMap.entries()) {
      const ffdNoteItems: FfdNoteItem[] = groupNotes.map((note) => ({
        id: note.id,
        title: note.title,
        date: note.createdAt,
        content: note.content,
        hasBeenCopied: note.hasBeenCopied,
      }));

      noteGroups.push({
        visitId: dateKey,
        notes: ffdNoteItems,
        siteId: dateKey,
        patientId: dateKey,
        groupDate: dateKey,
        createdDate: groupNotes[0]?.createdAt || dateKey,
        updatedDate: groupNotes[0]?.updatedAt || dateKey,
      });
    }

    // Sort by date descending (newest first)
    return noteGroups.sort((a, b) => b.groupDate.localeCompare(a.groupDate));
  }

  // Updaters
  public readonly setSearchTerm = this.updater(
    (state, searchTerm: string | null): FfdNotesState => ({
      ...state,
      searchTerm,
      offset: 0,
      expressNotes: [],
      totalCount: null,
    }),
  );

  public readonly setOffset = this.updater(
    (state, offset: number): FfdNotesState => ({
      ...state,
      offset,
    }),
  );

  public readonly setActiveNoteGroup = this.updater(
    (state, noteGroupId: string | null): FfdNotesState => ({
      ...state,
      activeNoteGroupId: noteGroupId,
    }),
  );

  public readonly setSelectedNote = this.updater(
    (state, noteId: string | null): FfdNotesState => ({
      ...state,
      selectedNoteId: noteId,
    }),
  );

  public readonly setEditedNoteId = this.updater(
    (state, noteId: string | null): FfdNotesState => ({
      ...state,
      editedNoteId: noteId,
    }),
  );

  public readonly setIsNoteSaving = this.updater(
    (state, isSaving: boolean): FfdNotesState => ({
      ...state,
      isNoteSaving: isSaving,
    }),
  );

  public readonly setIsNoteSaved = this.updater(
    (state, isSaved: boolean): FfdNotesState => ({
      ...state,
      isNoteSaved: isSaved,
    }),
  );

  public findNoteGroupByNoteId(noteGroups: FfdNoteGroup[], noteId: string): FfdNoteGroup | null {
    for (const group of noteGroups) {
      if (group.notes.some((note) => note.id === noteId)) {
        return group;
      }
    }
    return null;
  }

  // Effects
  public readonly getExpressNotes = this.effect((trigger$: Observable<{ silentReload: boolean } | void>) =>
    trigger$.pipe(
      withLatestFrom(this.state$),
      switchMap(([payload, state]) => {
        const silentReload = payload?.silentReload || false;
        if (silentReload) {
          this.patchState({ infiniteScrollStatus: DataState.Loading });
        } else {
          this.patchState({ listStatus: DataState.Loading });
        }
        const searchTerm = state.searchTerm || undefined;

        return this.expressNotesDataService.getExpressNotes(PAGE_SIZE, state.offset, searchTerm).pipe(
          tapResponse(
            (response) => {
              const newNotes = [...state.expressNotes, ...(response.items || [])];
              const noteGroups = this.groupNotesByDate(newNotes);
              const firstNote = noteGroups[0]?.notes[0];
              const shouldAutoSelect = !silentReload && !state.selectedNoteId && firstNote;

              this.patchState({
                expressNotes: newNotes,
                listStatus: DataState.Fetched,
                infiniteScrollStatus: DataState.Fetched,
                totalCount: response.totalCount,
                ...(shouldAutoSelect && {
                  activeNoteGroupId: noteGroups[0].visitId,
                  selectedNoteId: firstNote.id,
                }),
              });
            },
            (error) => {
              console.error('Error fetching express notes:', error);
              this.toastService.error(this.translateService.instant('common_error_message'));
              this.patchState({
                listStatus: DataState.Error,
                infiniteScrollStatus: DataState.Error,
                expressNotes: state.expressNotes, // Keep existing notes on error
              });
            },
          ),
        );
      }),
    ),
  );

  public readonly loadMoreNotes = this.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state$),
      tap(([, state]) => {
        if (state.listStatus === DataState.Loading || state.infiniteScrollStatus === DataState.Loading) {
          return;
        }

        if (state.totalCount !== null && state.expressNotes.length >= state.totalCount) {
          return;
        }

        this.setOffset(state.offset + PAGE_SIZE);
        this.getExpressNotes({ silentReload: true });
      }),
    ),
  );

  public searchNotes = this.effect((noteListSearchTerm$: Observable<string | null>) =>
    noteListSearchTerm$.pipe(
      debounceTime(SEARCH_DEBOUNCE_TIME_MS),
      tap((searchTerm) => {
        this.tagPusherService.pushTag({ event: TagEvent.FfdNotesNoteSearch });
        this.setSearchTerm(searchTerm);
      }),
      switchMap(() => {
        this.getExpressNotes();
        return this.state().expressNotes;
      }),
    ),
  );

  public updateNote = this.effect((update$: Observable<FfdNoteItem>) =>
    update$.pipe(
      tap(() => this.setIsNoteSaving(true)),
      switchMap(({ id, title, content, hasBeenCopied }) => {
        return this.expressNotesDataService.updateExpressNoteById(id, { title, content, hasBeenCopied }).pipe(
          tapResponse(
            () => {
              // Update the note in the local state without refetching
              const updatedExpressNotes = this.state().expressNotes.map((note) =>
                note.id === id
                  ? {
                      ...note,
                      ...(title !== undefined && { title }),
                      content,
                      hasBeenCopied,
                    }
                  : note,
              );

              this.patchState({
                expressNotes: updatedExpressNotes,
                isNoteSaved: true,
                isNoteSaving: false,
              });
            },
            (error) => {
              console.error('Error updating express note:', error);
              this.toastService.error(this.translateService.instant('common_error_message'));
              this.patchState({
                isNoteSaving: false,
                isNoteSaved: false,
              });
            },
          ),
        );
      }),
    ),
  );

  public deleteNote = this.effect((noteId$: Observable<string>) =>
    noteId$.pipe(
      withLatestFrom(this.state$),
      switchMap(([noteId, state]) => {
        return this.expressNotesDataService.deleteExpressNoteById(noteId).pipe(
          tapResponse(
            () => {
              // Remove the note from local state
              const updatedExpressNotes = state.expressNotes.filter((note) => note.id !== noteId);

              // Clear selection if the deleted note was selected
              const clearSelection = state.selectedNoteId === noteId;

              this.patchState({
                expressNotes: updatedExpressNotes,
                totalCount: state.totalCount === null ? null : state.totalCount - 1,
                ...(clearSelection && {
                  selectedNoteId: null,
                  activeNoteGroupId: null,
                  editedNoteId: null,
                }),
              });
            },
            (error) => {
              console.error('Error deleting express note:', error);
              this.toastService.error(this.translateService.instant('common_error_message'));
            },
          ),
        );
      }),
    ),
  );
}
